// English dictionary. Keys mirror ko.ts exactly.
// NOTE: lore/marketing copy (hero.*, lore.*, steps.*, oath.*) is a first
// draft — review for brand voice. Functional strings are literal translations.
export const en: Record<string, string> = {
  // ── meta ──
  'meta.title': 'DigMol — Dener Investiture · Chronicles of Eldorasia',

  // ── topbar / wallet ──
  'topbar.become': 'Become a Dener',
  'wallet.connect': 'Connect Wallet',
  'wallet.connected': 'Connected',
  'wallet.copy': 'Copy',
  'wallet.copied': 'Copied!',
  'wallet.myMol': 'My MOL',
  'wallet.denerDash': 'My Distributor Dashboard',
  'wallet.admin': 'Admin',
  'wallet.disconnect': 'Disconnect',
  'wallet.roleOwner': 'OWNER',
  'wallet.roleDist': 'Distributor',
  'wallet.connectCancelled': 'Wallet connection was cancelled.',
  'wallet.noWalletFallback': 'Could not connect the wallet. Please try again shortly.',
  'wallet.connectFail': 'Connection failed: {msg}',

  // ── mobile wallet choice (deeplink / WalletConnect) ──
  'wallet.mobileGuide': 'Choose a wallet to connect. MetaMask works here too.',
  'wallet.openInMetaMask': 'Open in MetaMask app (fallback)',
  'wallet.retryMetaMask': 'Open in MetaMask app again',
  'wallet.useMetaMaskApp':
    'The MetaMask app may already be open. Switch to the app to continue, or try again below.',
  'wallet.otherWallet': 'Connect another wallet',
  'wallet.cancel': 'Close',
  'wallet.stuckNotice':
    'The wallet is not responding. Please reload the page or restart the MetaMask app.',
  'wallet.reload': 'Reload',
  'wallet.connectWc': 'Connect wallet',
  'wallet.preparing': 'Preparing…',
  'wallet.deadSession': 'The wallet session expired. Please reload the page and try again.',

  // ── chain (network) warning ──
  'chain.wrong': 'You are not on BNB Smart Chain. Switch networks to continue.',
  'chain.switch': 'Switch to BSC',

  // ── in-app browser (KakaoTalk/LINE/Instagram…) escape guidance ──
  'inapp.title': 'Wallets cannot be connected inside {app}',
  'inapp.desc':
    'In-app browsers block wallet apps for security reasons. Open this page in an external browser to connect.',
  'inapp.open': 'Open in external browser',
  'inapp.copy': 'Copy link',
  'inapp.copied': 'Link copied. Paste it into your browser.',
  'inapp.copyFail': 'Copy failed. Long-press the address bar to copy it.',
  'inapp.manualOnly': 'Automatic opening is not supported. Copy the link and paste it into your browser.',
  'inapp.justBrowse': 'Just browse',
  'wallet.deeplinkFailed':
    'The MetaMask app did not open or did not connect. Please use [Connect wallet] below.',

  // ── language toggle ──
  'lang.toggleAria': 'Switch language',

  // ── hero / surface ──
  'alt.mole': 'MolePin mole',
  'hero.headline': 'A fallen kingdom calls<br><em>those who will guard it</em> again.',
  'hero.tagline': 'Eldorasia calls for those who will guard it again.',
  'hero.lead':
    '<b>Eldorasia</b>, the underground kingdom of seventeen golden mole tribes. After sixteen vanished, the last heir, MolePin, digs anew. A <b>Dener</b> stands beside him as the guardian leader of the vein — and that investiture begins by holding enough <b>MOL</b>.',
  'hero.scrollcue': 'Descend into the vein and receive your investiture',

  // ── lore (section 1) ──
  'lore.eyebrow': 'Layer I · Who is a Dener',
  'lore.headline': 'Not one who sells,<br>but one who <em>guards</em>.',
  'lore.quote': '“Many hands mine the vein, but few will guard it.”',
  'lore.lead':
    'A Dener is a <b>guardian leader</b> who has contributed to the MOL ecosystem and proven its worth. As MolePin forges chains from the souls of his kin, a Dener holds enough MOL to uphold the vein’s stability. The rite to reach this place is exactly <b style="color:var(--gold)">PV</b> — the Dener investiture.',
  'lore.pillar1.title': 'Right to Guard',
  'lore.pillar1.desc': 'Only one who holds 20 million MOL is invested as a Dener. The title is not a privilege but a duty.',
  'lore.pillar1.en': 'Guardianship, not entitlement.',
  'lore.pillar2.title': 'Proven Contribution',
  'lore.pillar2.desc': 'Investiture is etched on-chain. No one can fake it, and anyone can verify it.',
  'lore.pillar2.en': 'Proven on-chain. Verifiable by all.',
  'lore.pillar3.title': 'An Open Vein',
  'lore.pillar3.desc': 'At a fixed rate, you mine exactly what you put in. No bargaining, no favoritism.',
  'lore.pillar3.en': 'Fixed rate. No bargaining.',

  // ── steps (section 2) ──
  'steps.eyebrow': 'Layer II · The Path of Investiture',
  'steps.headline': 'In four steps,<br>you become a <em>guardian</em>.',
  'steps.tagline': 'Four steps to become a Dener.',
  'steps.s1.t': 'Stand at the entrance — Connect wallet',
  'steps.s1.d': 'Connect to BSC with MetaMask or a compatible wallet. On mobile, open this page in your wallet app’s built-in browser.',
  'steps.s2.t': 'Pour BNB into the vein',
  'steps.s2.d': 'Enter the BNB to exchange, and the MOL you will mine is calculated instantly at the rate etched into the contract.',
  'steps.s3.t': 'Swing the pickaxe — Sign',
  'steps.s3.d': 'One signature. The contract sends MOL to your wallet exactly at the rate.',
  'steps.s4.t': 'The light of investiture rises',
  'steps.s4.d': 'The moment your held MOL reaches 20 million, your Dener eligibility opens.',

  // ── oath (section 3) ──
  'oath.eyebrow': 'Layer III · Oath of the Vein',
  'oath.quote':
    '“The contract does not bargain.<br>As much as you put in, at the rate etched in — nothing more.”<span class="src">— THE VEIN, ELDORASIA</span>',
  'oath.lead':
    'This vein of investiture is entangled with no other contract in the ecosystem. One rate, one stock, and your signature. So it can be verified — separately, and safely.',

  // ── vein (section 4) ──
  'vein.eyebrow': 'The Deepest · THE VEIN',
  'vein.headline': 'You have reached the vein.',
  'vein.tagline': 'Where BNB becomes MOL. Where a guardian is made.',
  'panel.title': 'The <em>Vein of Investiture</em>',
  'panel.subtitle': 'PV INVESTITURE · ISOLATED VEIN',
  'panel.gateLabel': 'Toward Dener investiture — MOL held',
  'panel.gateLine': 'Investiture line <b>20,000,000 MOL</b>',

  // ── swap box ──
  'swap.bnbLabel': 'BNB to pour into the vein',
  'swap.molOutLabel': 'MOL to mine (contract rate)',
  'swap.rateNote': 'The rate is computed from the value etched into the contract.',
  'swap.statRate': 'Contract rate',
  'swap.statStock': 'Remaining vein',
  'swap.statMol': 'My MOL',
  'swap.contractMeta': 'Set the contract address to show the live rate and stock.',
  'cta.mine': 'Swing the pickaxe — Mine MOL',

  // ── status / investiture (shared by panel & modal) ──
  'status.pending': '○ Not invested',
  'status.dener': '● Dener invested',
  'inv.done': '⛏ <b>You have been invested as a Dener.</b> You now hold the guardian title of the vein.',
  'inv.remaining': 'You must hold <b>{amount} MOL</b> more to be invested.',

  // ── become-a-dener modal ──
  'modal.title': 'Dener Investiture <em>Rite</em>',
  'modal.subtitle': 'BECOME A DENER · BNB → MOL',
  'modal.close': 'Close',
  'modal.gateLabel': 'Toward Dener investiture',
  'modal.molRangeLabel': 'MOL amount to buy',
  'modal.molRangeOpt': '(min 20 million)',
  'modal.tick1': '20M',
  'modal.tick2': '100M',
  'modal.tick3': '200M',
  'modal.payLabel': 'BNB to pay',
  'modal.priceLoading': 'Loading price…',
  'modal.distLabel': 'Select distributor',
  'modal.distOpt': '(registered distributor)',
  'modal.distPlaceholder': 'e.g. alice.dex',
  'modal.outLabel': 'MOL to receive',
  'modal.outNote': 'Select a distributor to see the discounted amount.',
  'modal.noAddrMeta': '⚠ PV_GATE_ADDRESS not set — VITE_PV_GATE_ADDRESS required in .env',
  'modal.priceNoteNoAddr': 'Price shows after the contract address is set.',
  'modal.priceNote': 'MOL {mol} · BNB {bnb} (feed)',
  'modal.priceFail': 'Could not load the price.',
  'modal.needMolAmount': 'Set the MOL amount to buy.',
  'modal.noAddrErr': 'PV_GATE_ADDRESS is not set.',
  'modal.discountChip': '<span class="dk">Distributor discount (+{pct}%)</span><span class="dv">+{extra} MOL</span>',
  'modal.rateBase': 'Base {base} MOL + discount {extra} MOL',
  'modal.rewardLine': '<br>Distributor reward {rwd} BNB paid automatically',
  'modal.noDiscountReward': 'No discount · distributor reward {rwd} BNB paid automatically',
  'modal.distHint': 'Select a distributor to see discount/reward.',

  // ── buyer primary domain guidance (reverse lookup) ──
  'domain.connectFirst': 'Please connect your wallet first.',
  'domain.checking': 'Checking the primary domain of {addr}…',
  'domain.checkedWallet': 'Checked wallet: {addr}',
  'domain.noneTitle': 'No primary domain is linked to this wallet.',
  'domain.recheck': 'Check again',
  'domain.have': '✓ Your primary domain: <b>{name}</b>',
  'domain.guideTitle': 'Applying for Dener requires a dexignation.com primary domain.',
  'domain.step1': 'Get a domain at dexignation.com',
  'domain.step2': 'Set one domain as your primary domain',
  'domain.step3': 'Come back and apply for Dener',
  'domain.link': 'Go to dexignation.com ↗',

  // ── distributor address validation ──
  'dist.valid': '✓ Valid distributor — buyer discount {disc}% · distributor reward {rwd}%',
  'dist.validName': '✓ {name} → {addr} · buyer discount {disc}% · distributor reward {rwd}%',
  'dist.inactive': 'Inactive distributor. Proceeding without a discount.',
  'dist.notRegistered': 'Not a registered distributor. Proceeding without a discount.',
  'dist.required': 'Enter a valid .dex domain to proceed with the investiture.',
  'dist.requiredBadge': 'Required',
  'dist.requiredHint': 'A discount .dex domain is required.',
  'dist.notDexName': 'Enter a .dex name (e.g. alice.dex)',
  'dist.resolving': 'Resolving name…',
  'dist.nameNotFound': 'This name is not registered.',
  'dist.noBnbAddr': 'This name has no BNB address linked.',
  'dist.resolveError': 'Error resolving the name. Please try again shortly.',
  'dist.loadingDistributors': 'Loading distributors…',
  'dist.selectPlaceholder': 'Select a distributor',
  'dist.selectRequired': 'Distributor selection is required.',
  'dist.selectedDistributor': '✓ {name} · Dener discount {disc}% · distributor reward {rwd}%',
  'dist.selectedInactiveDistributor': 'Inactive distributor: {name}. Proceeding without a discount.',
  'dist.selectInvalid': 'The selected distributor cannot be used.',
  'dist.noDistributors': 'No distributors are registered.',
  'dist.listError': 'Could not load distributors. Please try again shortly.',

  // ── distributor dashboard ──
  'dener.title': 'My <em>Distributor</em> Dashboard',
  'dener.subtitle': 'DISTRIBUTOR DASHBOARD',
  'dener.discLabel': 'Buyer discount',
  'dener.rwdLabel': 'My reward',
  'dener.statusLabel': 'Status',
  'dener.setLabel': 'Set my discount rate',
  'dener.setOpt': '(0–15%, reward is automatic)',
  'dener.apply': 'Apply discount (sign)',
  'dener.lockedText': '🔒 The admin has set the discount/reward rate. Contact the admin to change it.',
  'dener.buyersHead': 'Buyers who chose me as their distributor',
  'dener.active': 'Active',
  'dener.inactive': 'Inactive',
  'dener.autoHint': '→ My reward {n}%',
  'dener.signConfirm': 'Confirm the signature…',
  'dener.applied': '✓ Discount rate {pct}% applied',
  'dener.buyerCount': '{n} buyers',
  'dener.noBuyers': 'No one has bought through you yet.',
  'dener.buyersFail': 'Query failed (may be an RPC limit)',

  // ── admin overlay ──
  'admin.title': 'Admin — <em>Distributor Management</em>',
  'admin.subtitle': 'OWNER ADMIN',
  'admin.regPlaceholder': 'Distributor .dex name or wallet address',
  'admin.regBtn': '+ Register',
  'admin.regRequired': 'Enter a distributor .dex name or wallet address.',
  'admin.regInvalid': 'Enter a .dex name or 0x wallet address.',
  'admin.regAddressOk': '✓ Wallet address confirmed: {addr}',
  'admin.regResolved': '✓ {name} → BNB {addr}',
  'admin.detailEmpty': '← Select a distributor',
  'admin.dBuyersK': 'Buyers',
  'admin.dDiscK': 'Discount',
  'admin.dRwdK': 'Reward',
  'admin.activeTitle': 'Activate distributor',
  'admin.activeDescDefault': 'When active, this distributor’s discount/reward applies',
  'admin.lockTitle': 'Admin lock',
  'admin.lockDescDefault': 'Locked: admin sets only / Unlocked: the distributor sets',
  'admin.discRangeLabel': 'Dener discount rate',
  'admin.rwdRangeLabel': 'Distributor reward rate',
  'admin.apply': 'Set this discount/reward (admin lock)',
  'admin.remove': 'Delete distributor',
  'admin.noDistributors': 'No distributors registered.<br>Register one above with a .dex name or wallet address.',
  'admin.listRates': 'Discount {disc}% · Reward {rwd}% · <span class="{cls}">{status}</span>',
  'admin.activeDescOn': 'Active — discount/reward applies',
  'admin.activeDescOff': 'Inactive — discount/reward does not apply',
  'admin.lockDescOn': 'Locked — only the admin can set the discount rate',
  'admin.lockDescOff': 'Unlocked — the distributor can set their own discount rate',
  'admin.regSign': 'Confirm the registration signature…',
  'admin.registered': '✓ Registered',
  'admin.setSign': 'Confirm the settings signature…',
  'admin.ratesSet': '✓ Set (locked)',
  'admin.toggleSign': 'Confirm signature…',
  'admin.activated': 'Activated',
  'admin.deactivated': 'Deactivated',
  'admin.toggleResult': '✓ {state}',
  'admin.lockApplied': '✓ Admin lock (discount {disc}% / reward {rwd}%)',
  'admin.released': '✓ Switched to distributor self-management',
  'admin.removeSign': 'Confirm the deletion signature…',
  'admin.removed': '✓ Deleted',

  // ── footer ──
  'footer.brand': 'Dig<em>Mol</em> — The Vein of Dener Investiture',
  'footer.line1': 'Chronicles of Eldorasia · PV investiture-only isolated exchange contract',
  'footer.tag': 'DigMolPvGate · Independent deployment · unrelated to the main ecosystem',
  'footer.disc': 'All information on this page is for reference only and is not investment advice. © 2026 MolePin Community',

  // ── contract / refresh ──
  'contract.noAddr': '⚠ PV_GATE_ADDRESS not set — add VITE_PV_GATE_ADDRESS to .env to enable real swaps.',
  'contract.vein': 'Vein {link}',
  'refresh.rateNote': 'MOL {mol} · BNB {bnb} · {stock} MOL remaining',
  'refresh.queryFail': 'Contract query failed: {msg}',

  // ── swap flow ──
  'swap.needAmount': 'Enter the amount of BNB to pour into the vein.',
  'swap.noAddr': 'PV_GATE_ADDRESS is not set (deployment address required).',
  'swap.failPrefix': 'Mining failed: {msg}',

  // ── tx states ──
  'tx.signing': 'Confirm the signature in your wallet…',
  'tx.pendingMining': 'The pickaxe strikes the vein… awaiting confirmation',
  'tx.confirming': 'Waiting for block confirmation…',
  'tx.successDefault': 'Done',
  'tx.successMined': '⛏ Mining succeeded — mined <b>{got}</b>',
  'tx.viewTx': 'View transaction ↗',

  // ── error maps ──
  'err.userRejected': 'The signature was rejected.',
  'err.insufficientBnb': 'Insufficient BNB balance (swap amount + gas).',
  'err.insufficientStock': 'The vein’s MOL stock is insufficient. Please try again shortly.',
  'err.gateClosed': 'The vein is currently closed.',
  'err.zeroAmount': 'The amount is zero.',
  'err.noGateAddr': 'The contract address is not set.',
  'err.tx.userCancelled': 'You cancelled the signature in your wallet.',
  'err.tx.insufficientGas': 'Insufficient gas (BNB). Please top up BNB in your wallet.',
  'err.tx.notOwner': 'No permission (run with the owner wallet).',
  'err.tx.notDist': 'Not a registered distributor.',
  'err.tx.ownerLocked': 'This distributor is admin-locked and cannot be changed.',
  'err.tx.badDiscount': 'Discount rate out of range (0–15%).',
  'err.tx.badReward': 'Reward rate out of range (0–5%).',
  'err.tx.network': 'Network error. Please try again shortly.',
  'err.tx.unknown': 'Something went wrong. Please try again shortly.',

  // ── ticker feed messages ──
  'feed.msg1': 'A new guardian has joined the kingdom',
  'feed.msg2': 'A Dener has received investiture',
  'feed.msg3': 'A guardian has settled into the fallen kingdom',
  'feed.msg4': 'One more light of Eldorasia shines',
  'feed.msg5': 'A guardian with a pickaxe has appeared',

  // ── shared ──
  'common.loading': 'Loading…',
  'common.txLink': 'tx ↗',
  'common.queryFail': 'Query failed',
};
